"use client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";

/**
 * A custom hook for managing search parameters in a Next.js application.
 * @returns An object containing methods to interact with search params.
 */
export function useQueryParams() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /**
   * Creates a new search params string by merging the current
   * search params with a provided key/value pair.
   * @param name - The name of the query parameter.
   * @param value - The value of the query parameter.
   * @returns A new search params string.
   */
  const createQueryString = useCallback(
    (name: string, value: string): string => {
      const params = new URLSearchParams(searchParams?.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams],
  );

  /**
   * Appends a new key/value pair to the search params and updates the URL.
   * @param name - The name of the query parameter to append.
   * @param value - The value of the query parameter to append.
   */
  const appendQueryParam = useCallback(
    (name: string, value: string): void => {
      const newSearchParams = new URLSearchParams(searchParams?.toString());
      newSearchParams.append(name, value);
      router.push(`${pathname}?${newSearchParams.toString()}`);
    },
    [pathname, router, searchParams],
  );

  /**
   * Updates an existing key/value pair in the search params or adds it if it doesn't exist, then updates the URL.
   * @param name - The name of the query parameter to update.
   * @param value - The new value of the query parameter.
   */
  const updateQueryParam = useCallback(
    (name: string, value: string): void => {
      const newSearchParams = new URLSearchParams(searchParams?.toString());
      newSearchParams.set(name, value);
      router.push(`${pathname}?${newSearchParams?.toString()}`);
    },
    [pathname, router, searchParams],
  );

  /**
   * Updates the entire URL with new search params.
   * @param newSearchParams - An object containing key/value pairs to set as new search params.
   */
  const updateUrl = useCallback(
    (newSearchParams: Record<string, string>): void => {
      const params = new URLSearchParams(searchParams?.toString());
      Object.entries(newSearchParams).forEach(([key, value]) => {
        params.set(key, value);
      });
      router.push(`${pathname}?${params?.toString()}`);
    },
    [pathname, router, searchParams],
  );

  return {
    searchParams,
    createQueryString,
    appendQueryParam,
    updateQueryParam,
    updateUrl,
  };
}
/**
 * A custom hook for managing the 'searchResult' query parameter that's given after a user inputs a public report search
 * This hook utilizes the `useQueryParams` hook for underlying functionality.
 *
 * @returns An object containing functions to update and retrieve the search result.
 */
export function useSearchResultQueryParams() {
  const { searchParams, updateQueryParam } = useQueryParams();

  /**
   * Updates the `'searchResult'` query parameter with a new value.
   *
   * @param searchResult - The new search result value to set.
   * @returns A function that updates the 'searchResult' query parameter.
   */
  const updateSearchResult = (searchResult: string) => {
    if (searchParams?.get("searchResult") === searchResult) return;
    return updateQueryParam("searchResult", searchResult);
  };

  /**
   * Retrieves the current value of the `'searchResult'` query parameter.
   *
   * @returns The current `'searchResult'` value, or null if not set.
   */
  const getSearchResult = (): string | null => {
    return searchParams?.get("searchResult") ?? null;
  };

  return {
    updateSearchResult,
    getSearchResult,
  };
}
